import { useState } from "react";
import { useNavigate } from "react-router-dom";
import usePwaInstall from "../hooks/usePwaInstall";
import { Download, Share2, CheckCircle2, Smartphone, MonitorSmartphone, MoreVertical } from "lucide-react";

const MESSENGER_LOGO = "/messenger-logo.png";

// Detect platform for the correct manual install steps
const getPlatform = () => {
  const ua = navigator.userAgent || "";
  const isIpadOS = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  const isIos = (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) || isIpadOS;
  const isAndroid = /Android/.test(ua);
  const isMobile = isIos || isAndroid;
  return { isIos, isAndroid, isMobile };
};

const InstallMessengerPage = () => {
  const navigate = useNavigate();
  const { installed, canInstall, isIos, promptInstall } = usePwaInstall();
  const { isAndroid, isMobile } = getPlatform();
  const [showManual, setShowManual] = useState(false);
  const [status, setStatus] = useState("");

  const handleInstall = async () => {
    const res = await promptInstall();
    if (res?.outcome === "accepted") {
      setStatus("Installing… check your home screen / apps.");
    } else if (res?.outcome === "unavailable" || res?.outcome === "error") {
      // Browser didn't offer the auto-prompt — show manual steps
      setShowManual(true);
    } else if (res?.outcome === "dismissed") {
      setStatus("Install dismissed — you can try again anytime.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a1f] via-[#1a0a3e] to-[#3a0a5c] flex flex-col items-center justify-start sm:justify-center p-5 py-10">
      <img src={MESSENGER_LOGO} alt="MEGA Chat Messenger" className="w-40 h-40 sm:w-52 sm:h-52 object-contain drop-shadow-2xl" data-testid="install-logo" />

      <h1 className="text-white font-black text-3xl sm:text-4xl mt-4 text-center">Get MEGA Chat Messenger</h1>
      <p className="text-purple-200 text-sm sm:text-base mt-2 text-center max-w-md">
        Install the Messenger app on your phone or computer for fast chats, voice &amp; video calls, and notifications.
      </p>

      <div className="w-full max-w-md mt-8 bg-white/95 rounded-3xl shadow-2xl p-6" data-testid="install-card">
        {installed ? (
          <div className="text-center" data-testid="install-already">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
            <p className="font-bold text-purple-900 mt-2">Messenger is already installed 🎉</p>
            <button
              onClick={() => navigate("/messenger")}
              className="mt-4 w-full py-3 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 text-white font-black"
              data-testid="install-open-btn"
            >
              Open Messenger
            </button>
          </div>
        ) : (
          <>
            {/* Primary install button — auto-prompt when the browser supports it */}
            {(canInstall || !isMobile) && !isIos && (
              <button
                onClick={handleInstall}
                className="w-full py-4 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white font-black text-lg flex items-center justify-center gap-2 shadow-lg hover:from-emerald-400 hover:to-emerald-600"
                data-testid="install-now-btn"
              >
                <Download className="w-5 h-5" /> Install Now
              </button>
            )}

            {status && <p className="text-center text-sm text-emerald-700 font-semibold mt-3" data-testid="install-status">{status}</p>}

            {/* Manual instructions (always available as a fallback / for iOS) */}
            {(isIos || showManual || (isMobile && !canInstall)) && (
              <div className="mt-4 space-y-3" data-testid="install-manual">
                {isIos ? (
                  <div className="bg-purple-50 rounded-2xl p-4">
                    <div className="flex items-center gap-2 text-purple-900 font-bold"><Smartphone className="w-4 h-4" /> On iPhone / iPad (Safari)</div>
                    <ol className="list-decimal ml-5 mt-2 text-sm text-purple-800 space-y-1">
                      <li>Tap the <b>Share</b> button <Share2 className="inline w-3.5 h-3.5" /> at the bottom of Safari.</li>
                      <li>Scroll down and tap <b>“Add to Home Screen”</b>.</li>
                      <li>Tap <b>Add</b> — the Messenger icon appears on your home screen.</li>
                    </ol>
                  </div>
                ) : isAndroid ? (
                  <div className="bg-purple-50 rounded-2xl p-4">
                    <div className="flex items-center gap-2 text-purple-900 font-bold"><MoreVertical className="w-4 h-4" /> On Android (Chrome)</div>
                    <ol className="list-decimal ml-5 mt-2 text-sm text-purple-800 space-y-1">
                      <li>Tap the <b>⋮</b> menu (top-right of Chrome).</li>
                      <li>Tap <b>“Install app”</b> or <b>“Add to Home screen”</b>.</li>
                      <li>Confirm — the Messenger icon appears on your home screen.</li>
                    </ol>
                  </div>
                ) : (
                  <div className="bg-purple-50 rounded-2xl p-4">
                    <div className="flex items-center gap-2 text-purple-900 font-bold"><MonitorSmartphone className="w-4 h-4" /> On Desktop (Chrome / Edge)</div>
                    <ol className="list-decimal ml-5 mt-2 text-sm text-purple-800 space-y-1">
                      <li>Look for the <b>install icon</b> (a monitor with a ↓ arrow) at the right edge of the address bar.</li>
                      <li>Click it, then click <b>Install</b>.</li>
                      <li>If you don’t see it: open the <b>⋮</b> menu → <b>“Install MEGA Chat Messenger…”</b>.</li>
                    </ol>
                  </div>
                )}
              </div>
            )}

            {/* Always offer a "show manual steps" link + open-in-browser option */}
            {!isIos && !showManual && (canInstall || !isMobile) && (
              <button onClick={() => setShowManual(true)} className="w-full mt-3 text-sm text-purple-700 font-semibold underline" data-testid="install-manual-toggle">
                Button not working? Show manual steps
              </button>
            )}

            <button
              onClick={() => navigate("/messenger")}
              className="mt-5 w-full py-3 rounded-full border-2 border-purple-300 text-purple-800 font-bold hover:bg-purple-50"
              data-testid="install-use-browser-btn"
            >
              Use in browser instead
            </button>
          </>
        )}
      </div>

      <p className="text-purple-300/70 text-xs mt-6 text-center max-w-sm">
        Tip: after uninstalling, browsers sometimes hide the auto-install button for a while. The manual steps above always work.
      </p>
    </div>
  );
};

export default InstallMessengerPage;
